import { useEffect, useState } from 'react';
import { makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import blogService from '../../services/BlogService';
import tagService from '../../services/TagService';
import Hero from '../../components/hero/Hero';
import { useTranslation } from 'react-i18next';
import Categories from './Categories';
import FeaturedBlogCard from './FeaturedBlogCard';
import BlogCard from './BlogCard';
import Status from '../../enums/Status';
import IEntryPreivew from '../../models/blog/IEntryPreview';
import IBlogTag from '../../models/blog/IBlogTag';
import { useHeroMainSnap } from '../../hooks/useHeroMainSnap';
import SEO from '../../app/SEO';

const useStyles = makeStyles({
    mainContainer: {
        padding: 0,
        backgroundColor: '#f5f5f5',
    },
    blogs: {
        gap: "16px",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "start",
        width: "calc(100% - 2rem)",
        maxWidth: "1200px",
        margin: "1rem auto",
        padding: "0 1rem",
    },
    skeleton: {
        width: 'calc(33% - 8px)',
        margin: '0',
        "&:first-of-type": {
            width: '100%'
        },
        "@media (max-width: 768px)": {
            width: '100%'
        }
    },
    skeletonItem: {
        width: "100%",
        height: "300px",
    }
});

const Blogs = () => {
    const { t } = useTranslation("blog");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [blogs, setBlogs] = useState<IEntryPreivew[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [availableTags, setAvailableTags] = useState<IBlogTag[]>([]);

    useEffect(() => {
        fetchBlogs(setBlogs, setStatus);

        tagService.getTags()
            .then(setAvailableTags)
            .catch(() => setAvailableTags([]));
    }, []);

    useHeroMainSnap("blogs-hero", "blogs-main", {
        duration: 900,
        easing: 'quart',
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    const onSelectedCategoriesChange = (categories: string[]) =>
        setSelectedCategories(categories);

    const filteredBlogs =
        selectedCategories.length === 0
            ? blogs
            : blogs.filter(blog =>
                selectedCategories.some(category => {
                    const managedTag = availableTags.find(
                        tag => tag.name.toLowerCase() === category.toLowerCase()
                    );

                    const matchesLegacyTag =
                        blog.tags?.some(tag =>
                            tag.toLowerCase() === category.toLowerCase()
                        ) ?? false;

                    const matchesTagId =
                        managedTag
                            ? blog.tagIds?.includes(managedTag.id) ?? false
                            : false;

                    return matchesLegacyTag || matchesTagId;
                })
            );

    return (
        <>
            <SEO
                title={{
                    pageTitle: t('hero.pageTitle'),
                    title: t('hero.pageTitle')
                }}
                metaDescription={t('hero.metaDescription')}
                canonicalPath="/blog"
            />

            <div className={styles.mainContainer}>
                <div id="blogs-hero">
                    <Hero
                        title={t('hero.title')}
                        subtitle={t('hero.description')}
                        imageUri="/img/banners/blogs.jpg"
                    />
                </div>

                <div id="blogs-main">
                    <Categories
                        selectedCategories={selectedCategories}
                        setSelectedCategories={onSelectedCategoriesChange}
                    />

                    <div className={styles.blogs}>
                        {status === Status.LOADING &&
                            Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton
                                    key={i}
                                    className={styles.skeleton}
                                >
                                    <SkeletonItem
                                        className={styles.skeletonItem}
                                        shape="rectangle"
                                    />
                                </Skeleton>
                            ))
                        }

                        {status === Status.LOAD_SUCCESS &&
                            blogs.length > 0 &&
                            selectedCategories.length === 0 &&
                            <FeaturedBlogCard blog={blogs[0]} />
                        }

                        {status === Status.LOAD_SUCCESS &&
                            filteredBlogs.length > 0 &&
                            (
                                selectedCategories.length === 0
                                    ? blogs.slice(1)
                                    : filteredBlogs
                            ).map(blog => (
                                <BlogCard
                                    key={blog.id}
                                    blog={blog}
                                />
                            ))
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default Blogs;

async function fetchBlogs(
    setBlogs: (blogs: IEntryPreivew[]) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const blogs = await blogService.getBlogPreviews();

        setBlogs(blogs);
        setStatus(Status.LOAD_SUCCESS);
    } catch {
        setStatus(Status.LOAD_ERROR);
    }
}